import type { Query } from "mongoose";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";

export enum PositionDepthSource {
    DEPTH_FIELD = "DEPTH_FIELD",
    POSITION_DEPTH = "POSITION_DEPTH",
}

type PointDocument = {
    latitude?: number;
    longitude?: number;
    depth?: number;
    position?: VertexInfo;
};

const POSITION_PATH = "position";

const isPositionPath = (path: string) => path === POSITION_PATH || path.startsWith(`${POSITION_PATH}.`);

function writesPosition(update: Record<string, unknown>): boolean {
    return Object.entries(update).some(([key, value]) =>
        key.startsWith("$")
            ? value != null && typeof value === "object" && Object.keys(value).some(isPositionPath)
            : isPositionPath(key),
    );
}

export const addPositionSyncSaveHook = (depthSource: PositionDepthSource): ["save", (this: PointDocument) => void] => [
    "save",
    function (this: PointDocument) {
        if (typeof this.latitude !== "number" || typeof this.longitude !== "number") return;
        const depth = (depthSource === PositionDepthSource.DEPTH_FIELD ? this.depth : this.position?.depth) ?? 0;
        if (
            this.position?.lat === this.latitude &&
            this.position?.lng === this.longitude &&
            this.position?.depth === depth
        ) {
            return;
        }
        this.position = new VertexInfo({ lat: this.latitude, lng: this.longitude, depth });
    },
];

export const addPositionSyncUpdateHook = (
    depthSource: PositionDepthSource,
): [["updateOne", "findOneAndUpdate", "updateMany"], (this: Query<unknown, unknown>) => void] => [
    ["updateOne", "findOneAndUpdate", "updateMany"],
    function (this: Query<unknown, unknown>) {
        const update = this.getUpdate();
        if (update == null || Array.isArray(update)) return;
        // Mongo rejects an update that writes both a path and its parent or child.
        if (writesPosition(update)) return;
        const set = (update.$set ?? {}) as Record<string, unknown>;
        const fieldToPositionPath: [string, string][] = [
            ["latitude", "position.lat"],
            ["longitude", "position.lng"],
            ...(depthSource === PositionDepthSource.DEPTH_FIELD
                ? [["depth", "position.depth"] as [string, string]]
                : []),
        ];
        const positionSet = Object.fromEntries(
            fieldToPositionPath
                .map(([field, positionPath]) => [positionPath, set[field] ?? update[field]])
                .filter(([, value]) => value != null),
        );
        if (Object.keys(positionSet).length === 0) return;
        this.setUpdate({ ...update, $set: { ...set, ...positionSet } });
    },
];
